import { createFileRoute } from "@tanstack/react-router";
import { useState, useCallback, useMemo } from "react";

interface ModuleInfo {
  size: number;
  gzippedSize: number;
  path: string;
  deps: string[];
  importedBy?: string[];
}

interface SourceGraph {
  entryPoints: string[];
  modules: Record<string, ModuleInfo>;
}

interface TreeNode {
  path: string;
  size: number;
  gzippedSize: number;
  totalSize: number;
  totalGzippedSize: number;
  children: TreeNode[];
}

interface PathToModule {
  entryPoint: string;
  path: string[];
}

function debounce<T extends (...args: any[]) => any>(func: T, wait: number): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

function SourceGraphAnalyzer() {
  const [graph, setGraph] = useState<SourceGraph | null>(null);
  const [sortBy, setSortBy] = useState<"size" | "gzippedSize">("size");
  const [activeTab, setActiveTab] = useState<"tree" | "list">("tree");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedModule, setSelectedModule] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [pathCache, setPathCache] = useState<Record<string, PathToModule[]>>({});
  const [expandedEntryPoints, setExpandedEntryPoints] = useState<Set<string>>(new Set());

  const handleFileDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      const text = await file.text();
      const parsed = JSON.parse(text) as SourceGraph;

      // Precompute reverse dependencies
      const modules = { ...parsed.modules };
      Object.keys(modules).forEach((path) => {
        modules[path] = { ...modules[path], importedBy: [] };
      });

      Object.entries(modules).forEach(([path, module]) => {
        module.deps.forEach((dep) => {
          if (!modules[dep]) {
            return;
          }
          if (!modules[dep].importedBy) {
            modules[dep].importedBy = [];
          }
          modules[dep].importedBy.push(path);
        });
      });

      setGraph({ ...parsed, modules });
      setPathCache({});
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const debouncedSetSearchQuery = useMemo(() => debounce((query: string) => setSearchQuery(query), 150), []);

  const findPathsToModule = useCallback(
    (modulePath: string): PathToModule[] => {
      if (!graph) {
        return [];
      }

      // Check cache first
      if (pathCache[modulePath]) {
        return pathCache[modulePath];
      }

      // get the module

      // is the module invalid, return an empty array
      // is the recursion depth reached? return an empty array
      // is the module visited? return an empty array
      // is the module an entry point? return the module path in an array

      // mark the module as visited
      // for each importing module, recurse on it

      const visited = new Set<string>();
      const MAX_PATH_LENGTH = 20;
      const entryPointSet = new Set(graph.entryPoints);

      // DFS function to find all paths with memoization
      const dfs = (current: string, path: string[], depth = 0): string[][] => {
        const module = graph.modules[current];
        if (!module) {
          return [];
        }

        if (depth > MAX_PATH_LENGTH) {
          return [];
        }

        if (visited.has(current)) {
          return [];
        }

        visited.add(current);

        if (entryPointSet.has(current)) {
          return [[current, ...path]];
        }

        if (!module.importedBy) {
          return [];
        }

        return [...module.importedBy.flatMap((p) => dfs(p, [current, ...path], depth + 1))];
      };

      const paths = dfs(modulePath, []).map((path) => ({ entryPoint: path[0], path: path }));

      // Cache the final results
      setPathCache((prev) => ({
        ...prev,
        [modulePath]: paths,
      }));

      return paths;
    },
    [graph, pathCache],
  );

  const handleModuleClick = useCallback(
    (path: string) => {
      setIsLoading(true);
      setSelectedModule(path);

      // Use setTimeout to allow the UI to update before starting the heavy computation
      setTimeout(() => {
        findPathsToModule(path);
        setIsLoading(false);
      }, 0);
    },
    [findPathsToModule],
  );

  const buildTree = useCallback(
    (path: string, modules: Record<string, ModuleInfo>, visited = new Set<string>()): TreeNode => {
      if (visited.has(path)) {
        return {
          path,
          size: 0,
          gzippedSize: 0,
          totalSize: 0,
          totalGzippedSize: 0,
          children: [],
        };
      }
      visited.add(path);

      const module = modules[path];
      if (!module) {
        return {
          path,
          size: 0,
          gzippedSize: 0,
          totalSize: 0,
          totalGzippedSize: 0,
          children: [],
        };
      }

      const children = module.deps.map((dep) => buildTree(dep, modules, visited));
      const totalSize = children.reduce((sum, child) => sum + child.totalSize, module.size);
      const totalGzippedSize = children.reduce((sum, child) => sum + child.totalGzippedSize, module.gzippedSize);

      return {
        path,
        size: module.size,
        gzippedSize: module.gzippedSize,
        totalSize,
        totalGzippedSize,
        children: children.sort((a, b) => {
          if (sortBy === "size") {
            return b.totalSize - a.totalSize;
          }
          return b.totalGzippedSize - a.totalGzippedSize;
        }),
      };
    },
    [sortBy],
  );

  const formatSize = (bytes: number): string => {
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const TreeNode = ({ node, depth = 0 }: { node: TreeNode; depth?: number }) => {
    const [expanded, setExpanded] = useState(false);

    return (
      <div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            cursor: node.children.length > 0 ? "pointer" : "default",
            padding: "4px 0",
            whiteSpace: "nowrap",
            backgroundColor: "transparent",
            transition: "background-color 0.2s ease",
            marginLeft: `${depth * 12}px`,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#f5f5f5";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
          }}
          onClick={() => node.children.length > 0 && setExpanded(!expanded)}
        >
          {node.children.length > 0 && <span style={{ marginRight: "8px" }}>{expanded ? "▼" : "▶"}</span>}
          <span style={{ flex: 1, fontFamily: "monospace" }}>{node.path}</span>
          <span style={{ marginLeft: "16px", color: "#666" }}>
            {formatSize(sortBy === "size" ? node.totalSize : node.totalGzippedSize)}
          </span>
        </div>
        {expanded && node.children.map((child) => <TreeNode key={child.path} node={child} depth={depth + 1} />)}
      </div>
    );
  };

  const filteredModules = useMemo(() => {
    if (!graph) {
      return [];
    }
    return Object.entries(graph.modules)
      .filter(([path]) => path.toLowerCase().includes(searchQuery.toLowerCase()))
      .sort(([, a], [, b]) => {
        if (sortBy === "size") {
          return b.size - a.size;
        }
        return b.gzippedSize - a.gzippedSize;
      });
  }, [graph, searchQuery, sortBy]);

  const pathsToSelectedModule = useMemo(() => {
    if (!selectedModule || !graph) {
      return [];
    }
    return findPathsToModule(selectedModule);
  }, [selectedModule, graph, findPathsToModule]);

  const toggleEntryPoint = useCallback((entryPoint: string) => {
    setExpandedEntryPoints((prev) => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(entryPoint)) {
        newExpanded.delete(entryPoint);
      } else {
        newExpanded.add(entryPoint);
      }
      return newExpanded;
    });
  }, []);

  const renderImportPaths = () => {
    if (isLoading) {
      return <div>Loading paths...</div>;
    }
    if (pathsToSelectedModule.length === 0) {
      return <div>No import paths found</div>;
    }

    // Group paths by entry point
    const pathsByEntryPoint = new Map<string, PathToModule[]>();
    pathsToSelectedModule.forEach((pathInfo) => {
      const paths = pathsByEntryPoint.get(pathInfo.entryPoint) || [];
      paths.push(pathInfo);
      pathsByEntryPoint.set(pathInfo.entryPoint, paths);
    });

    const totalPaths = pathsToSelectedModule.length;

    return (
      <>
        <div style={{ marginBottom: "8px", fontFamily: "monospace" }}>Total paths to entry points: {totalPaths}</div>
        {Array.from(pathsByEntryPoint.entries()).map(([entryPoint, paths]) => (
          <div key={entryPoint} style={{ marginBottom: "16px" }}>
            <div
              onClick={() => toggleEntryPoint(entryPoint)}
              style={{
                cursor: "pointer",
                userSelect: "none",
                display: "flex",
                alignItems: "center",
                marginBottom: "4px",
              }}
            >
              <span style={{ marginRight: "8px" }}>{expandedEntryPoints.has(entryPoint) ? "▼" : "▶"}</span>
              <span style={{ fontWeight: "bold" }}>
                {entryPoint} ({paths.length} path{paths.length !== 1 ? "s" : ""})
              </span>
            </div>
            {expandedEntryPoints.has(entryPoint) && (
              <div style={{ marginLeft: "24px" }}>
                {paths.map((pathInfo, pathIndex) => (
                  <div
                    key={pathIndex}
                    style={{
                      fontFamily: "monospace",
                      marginBottom: "8px",
                    }}
                  >
                    {pathInfo.path.map((p, i) => (
                      <div key={i} style={{ marginLeft: `${i * 12}px` }}>
                        {i === pathInfo.path.length - 1 ? "└─ " : "├─ "}
                        {p}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </>
    );
  };

  const ContentView = () => {
    if (!graph) {
      return null;
    }

    if (activeTab === "tree") {
      return (
        <div style={{ minWidth: "max-content" }}>
          {graph.entryPoints
            .map((path) => buildTree(path, graph.modules))
            .sort((a, b) => {
              if (sortBy === "size") {
                return b.totalSize - a.totalSize;
              }
              return b.totalGzippedSize - a.totalGzippedSize;
            })
            .map((node) => (
              <TreeNode key={node.path} node={node} />
            ))}
        </div>
      );
    }

    if (selectedModule) {
      return (
        <div style={{ minWidth: "max-content" }}>
          <div style={{ marginBottom: "16px", display: "flex", alignItems: "center" }}>
            <button
              onClick={() => setSelectedModule(null)}
              style={{
                marginRight: "16px",
                padding: "4px 8px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              ← Back
            </button>
            <h2 style={{ margin: 0 }}>{selectedModule}</h2>
          </div>
          <div style={{ marginBottom: "16px" }}>
            <h3 style={{ margin: "0 0 8px 0" }}>Module Details</h3>
            <div style={{ fontFamily: "monospace" }}>
              <div>Size: {formatSize(graph.modules[selectedModule].size)}</div>
              <div>Gzipped Size: {formatSize(graph.modules[selectedModule].gzippedSize)}</div>
            </div>
          </div>
          <div style={{ marginBottom: "16px" }}>
            <h3 style={{ margin: "0 0 8px 0" }}>Dependencies</h3>
            <div style={{ fontFamily: "monospace", marginBottom: "8px" }}>
              {(() => {
                const tree = buildTree(selectedModule, graph.modules);
                const totalDeps = tree.children.length;
                const totalSize = tree.children.reduce((sum, child) => sum + child.totalSize, 0);
                const totalGzippedSize = tree.children.reduce((sum, child) => sum + child.totalGzippedSize, 0);
                return (
                  <>
                    <div>Total Dependencies: {totalDeps}</div>
                    <div>Total Size: {formatSize(totalSize)}</div>
                    <div>Total Gzipped Size: {formatSize(totalGzippedSize)}</div>
                  </>
                );
              })()}
            </div>
            <div style={{ fontFamily: "monospace" }}>
              {buildTree(selectedModule, graph.modules).children.map((child) => (
                <TreeNode key={child.path} node={child} />
              ))}
            </div>
          </div>
          <div>
            <h3 style={{ margin: "0 0 8px 0" }}>Import Paths</h3>
            {renderImportPaths()}
          </div>
        </div>
      );
    }

    return (
      <div style={{ minWidth: "max-content" }}>
        {filteredModules.map(([path, module]) => (
          <div
            key={path}
            style={{
              display: "flex",
              alignItems: "center",
              padding: "4px 0",
              whiteSpace: "nowrap",
              backgroundColor: "transparent",
              transition: "background-color 0.2s ease",
              cursor: "pointer",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#f5f5f5";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
            }}
            onClick={() => handleModuleClick(path)}
          >
            <span style={{ flex: 1, fontFamily: "monospace" }}>{path}</span>
            <span style={{ marginLeft: "16px", color: "#666" }}>
              {formatSize(sortBy === "size" ? module.size : module.gzippedSize)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{ padding: "20px" }}>
      <h1>Source Graph Analyzer</h1>
      <div
        style={{
          border: "2px dashed #ccc",
          padding: "20px",
          textAlign: "center",
          marginBottom: "20px",
        }}
        onDrop={handleFileDrop}
        onDragOver={handleDragOver}
      >
        <p>Drag and drop a source-graph.json file here</p>
      </div>

      {graph && (
        <>
          <div style={{ marginBottom: "20px", display: "flex", alignItems: "center", gap: "16px" }}>
            <div>
              <label>
                Sort by:
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as "size" | "gzippedSize")}
                  style={{ marginLeft: "8px" }}
                >
                  <option value="size">Size</option>
                  <option value="gzippedSize">Gzipped Size</option>
                </select>
              </label>
            </div>
            <div>
              <label>
                View:
                <select
                  value={activeTab}
                  onChange={(e) => setActiveTab(e.target.value as "tree" | "list")}
                  style={{ marginLeft: "8px" }}
                >
                  <option value="tree">Tree View</option>
                  <option value="list">List View</option>
                </select>
              </label>
            </div>
            {activeTab === "list" && (
              <div>
                <label>
                  Search:
                  <input
                    type="text"
                    onChange={(e) => debouncedSetSearchQuery(e.target.value)}
                    style={{ marginLeft: "8px" }}
                    placeholder="Search modules..."
                  />
                </label>
              </div>
            )}
          </div>

          <div
            style={{
              border: "1px solid #eee",
              padding: "20px",
              borderRadius: "4px",
              overflowX: "auto",
            }}
          >
            <ContentView />
          </div>
        </>
      )}
    </div>
  );
}

export const Route = createFileRoute("/debug/source-graph-analyzer")({
  component: SourceGraphAnalyzer,
});
